import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Box, InputAdornment, TextField } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { useIntl } from 'react-intl';

import IconButton from '@mui/material/IconButton';
import AddCommentOutlinedIcon from '@mui/icons-material/AddCommentOutlined';
import ClearIcon from '@mui/icons-material/Clear';
import FolderOpenOutlinedIcon from '@mui/icons-material/FolderOpenOutlined';
import ReceiptOutlinedIcon from '@mui/icons-material/ReceiptOutlined';
import GroupChannelList from '@sendbird/uikit-react/GroupChannelList';
import CreateChannel from '@sendbird/uikit-react/CreateChannel';
import { useSendbirdStateContext } from '@sendbird/uikit-react/useSendbirdStateContext';
import { QueryType } from '@sendbird/chat/groupChannel';
import type { GroupChannel } from '@sendbird/chat/groupChannel';

import { userProfileSelector } from '@/domain/env';
// eslint-disable-next-line no-restricted-syntax
import { loadedDataCompanyIdSelector, documentDocumentIdSelector } from '@/domain/documents';
import {
  totalUnreadCountSelector,
  companyUnreadCounterSelector,
  documentUnreadCountSelector,
  chatParticipantsSelector,
} from '@/domain/chat';
import BetaBadge from '@/components/BetaBadge';
import Dialog from '@/components/mui/Dialog';
import Tooltip from '@/components/mui/Tooltip';
import type { ChannelLike } from '@/components/mui/Layouts/components/Chat/types';
import { buildChannelContext, channelMatchesQuery, resolveChannelContext } from '@/components/mui/Layouts/components/Chat/helpers';
import { Apps } from '@/domain/env/helpers';
import CreateChannelModal from '@/components/mui/Layouts/components/Chat/components/CreateChannelModal';
import ChatDocumentPreviewModal from '@/components/mui/Layouts/components/Chat/components/ChatDocumentPreviewModal';
import ChannelPreviewItem from '@/components/mui/Layouts/components/Chat/components/ChannelList/ChannelPreviewItem';
import ChannelListEmptyState from '@/components/mui/Layouts/components/Chat/components/ChannelList/ChannelListEmptyState';
import UserDirectorySection from '@/components/mui/Layouts/components/Chat/components/ChannelList/UserDirectorySection';
import useChatUserDirectory from '@/components/mui/Layouts/components/Chat/hooks/useChatUserDirectory';
import {
  ChannelListFilterBox,
  ChannelListHeaderUsername,
  ChannelListRoot,
  ChannelListSearchBox,
  ChatListHeader,
  EmptyChatsText,
  FilterChip,
  FilterChipBar,
  PanelScrollArea,
  SectionHeader,
  UnreadCountBadge,
  UnreadPill,
  GroupChannelListWrapper,
} from '@/components/mui/Layouts/components/Chat/components/ChannelList/StyledComponents';

export const CURRENT_CONTEXT_FILTER = 'CURRENT_CONTEXT';

// Stable no-op for the non-context list's onEmptyChange (only the current-context list drives the
// "No chats" placeholder). Module-level so its identity never changes across renders.
const noop = (): void => {};

// Lift the "New chat" tooltip Popper above the chat Drawer (zIndex 1600) so it is
// visible — including the explanation shown while the button is disabled (DA-19426).
// Matches the 1700 used by the chat header tooltips and dialogs.
const createTooltipSlotProps = { popper: { sx: { zIndex: 'chatPopup' } } } as const;

interface Props {
  selectedChannelUrl: string;
  onSelect: (url: string) => void;
  onCreatingChange?: (isCreating: boolean) => void;
  sx?: SxProps<Theme>;
  /** Mobile view — renders company names as plain text in channel rows (see ChannelPreviewItem). */
  isMobile?: boolean;
}

const ChannelListPanel: React.FC<Props> = ({
  selectedChannelUrl,
  onSelect,
  onCreatingChange,
  sx,
  isMobile = false,
}) => {
  const { formatMessage } = useIntl();
  const userProfile = useSelector(userProfileSelector);
  const chatParticipants = useSelector(chatParticipantsSelector);
  // Chat follows the company whose data the store holds, not the one in the URL: it keeps the
  // last company on pages that name none, and waits out a company switch until the new
  // company's chat users have loaded. A known compromise, to revisit together with the profile.
  const companyId = useSelector(loadedDataCompanyIdSelector);
  const documentId = useSelector(documentDocumentIdSelector);
  const totalUnreadCount = useSelector(totalUnreadCountSelector);
  // Use the v3/v4-aware facade selector and derive the current-company count locally,
  // rather than reaching into the v4-only `currentCompanyUnreadSelector` (Copilot).
  const companyUnreadMap = useSelector(companyUnreadCounterSelector) as
    | { get: (key: string | null) => number | undefined }
    | undefined;
  const currentCompanyUnread = (companyId && companyUnreadMap?.get(companyId)) || 0;
  // Unread for the currently open document (sum across its channels). Used for the context
  // chip when a document is open — otherwise the chip shows the company total.
  const currentDocumentUnread = useSelector(documentUnreadCountSelector) as number;
  // The context chip's label switches between "Current document" and "Current company"
  // based on documentId; its COUNT must match — document count on a document page, else
  // company count.
  const contextUnread = documentId ? currentDocumentUnread : currentCompanyUnread;

  // Single source of truth for "there is a current company/document context".
  // Drives both channel creation (can only create inside a company) and whether
  // the filter chip bar is shown — without context, "All" would be the only chip
  // (nothing to filter to), so the whole bar is hidden.
  const hasCompanyContext = Boolean(companyId);

  const canCreateChannel = hasCompanyContext;
  const createTooltipId = canCreateChannel
    ? 'chat.channelList.createTooltip'
    : !companyId
      ? 'chat.channelList.createOutsideContextTooltip'
      : 'chat.channelList.createDisabledTooltip';

  const sendbirdState = useSendbirdStateContext();
  const sdk = sendbirdState.stores.sdkStore.sdk;

  const [search, setSearch] = useState<string>('');
  const [debouncedSearch, setDebouncedSearch] = useState<string>('');

  useEffect(() => {
    const next = search.length >= 2 ? search : '';
    if (next === '') {
      setDebouncedSearch('');
      return;
    }
    const timer = setTimeout(() => setDebouncedSearch(next), 300);
    return () => clearTimeout(timer);
  }, [search]);
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  // Whether the current-context chat list is visually empty (all channels hidden by the client-side
  // filters). Reported up from ChannelListEmptyState so the "No chats" placeholder is rendered here
  // as a normal sibling between the chat list and the Users section (predictable divider/layout),
  // not portaled into Sendbird's list body (DA-19646).
  const [isContextChatListEmpty, setIsContextChatListEmpty] = useState<boolean>(false);
  const [previewDoc, setPreviewDoc] = useState<{ companyId: string; documentId: string } | null>(null);
  const [pendingDeleteUrl, setPendingDeleteUrl] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [activeFilter, setActiveFilter] = useState<string>(companyId ? CURRENT_CONTEXT_FILTER : 'ALL');
  // Lazy keep-alive: a filter's list is mounted only once the user first opens that tab, then
  // kept alive so its scroll position survives (DA-19468). This bounds the number of concurrent
  // Sendbird collections to the tabs actually visited rather than all tabs that exist, which
  // matters as the chip bar grows (AP/Close/Pay). Seed with the initially-active filter.
  const [mountedFilters, setMountedFilters] = useState<Set<string>>(
    () => new Set([companyId ? CURRENT_CONTEXT_FILTER : 'ALL']),
  );

  const contextChipLabel = documentId
    ? formatMessage({ id: 'chat.channelList.filterCurrentDocument', defaultMessage: 'Current document' })
    : formatMessage({ id: 'chat.channelList.filterCurrentCompany', defaultMessage: 'Current company' });

  const isCurrentContextFilter = activeFilter === CURRENT_CONTEXT_FILTER;

  // The "current context" chip is only rendered when companyId exists. If the
  // user selects it and then navigates out of company context, activeFilter
  // would stay CURRENT_CONTEXT while matchesCurrentContext() always returns
  // false (no companyId) — rendering an empty list with no active chip. Reset
  // to ALL when the company context is lost.
  useEffect(() => {
    if (!companyId && isCurrentContextFilter) {
      setActiveFilter('ALL');
      // The active tab must always be mounted — flipping to ALL here bypasses
      // handleFilterChange, so mount ALL explicitly.
      setMountedFilters((prev) => (prev.has('ALL') ? prev : new Set(prev).add('ALL')));
    }
  }, [companyId, isCurrentContextFilter]);

  // One list is kept mounted per filter so switching tabs preserves each tab's scroll
  // position and loaded pages (DA-19468). The "current context" list only exists when
  // there is a company context — without it the chip bar is hidden and "All" is the only
  // filter (see hasCompanyContext).
  const visibleFilters = useMemo(
    () => (hasCompanyContext ? ['ALL', CURRENT_CONTEXT_FILTER] : ['ALL']),
    [hasCompanyContext],
  );

  // Query params are per-filter (each mounted list owns a fixed filter), so tab switches
  // never change a list's params and never force a Sendbird re-query/remount. Search still
  // changes params for every list, but that is driven through the list `key` below so a
  // search resets the (visible) list to the top — which is the intended behaviour.
  const buildQueryParams = React.useCallback(
    (filter: string) => {
      const isCtx = filter === CURRENT_CONTEXT_FILTER;
      // Current-context tab: narrow SERVER-SIDE by the channel-context metadata written on create
      // (buildChannelContext). Without this the query returns the newest N channels company-wide
      // (customTypesFilter=AP only) and matchesCurrentContext drops the non-matching ones client-side
      // — so a document/company channel that sorts past the loaded page is never fetched and the list
      // shows "No chats" even though the channel exists (DA: new chat missing from Current-document).
      // On a document page filter by the composite `documentKey` (`<companyId>/<documentId>`); on a
      // company page filter by `companyId`. matchesCurrentContext still runs as a client-side safety net.
      const contextMetaFilter =
        isCtx && companyId
          ? documentId
            ? { metaDataKeyFilter: 'documentKey', metaDataValuesFilter: [`${companyId}/${documentId}`] }
            : { metaDataKeyFilter: 'companyId', metaDataValuesFilter: [companyId] }
          : {};
      return {
        includeEmpty: true,
        // With the server-side context filter the result is already scoped to this document/company,
        // so 100 comfortably covers every matching channel on the first page.
        limit: 100,
        ...(debouncedSearch
          ? { searchFilter: { query: debouncedSearch, fields: ['member_nickname', 'channel_name'] } }
          : {}),
        // Current context pre-filters by AP (context is AP-based); other app filters pass through as-is.
        ...(isCtx ? { customTypesFilter: [Apps.AP] } : filter !== 'ALL' && { customTypesFilter: [filter] }),
        ...contextMetaFilter,
      };
    },
    [debouncedSearch, companyId, documentId],
  );

  const matchesCurrentContext = React.useCallback(
    (channel: Pick<ChannelLike, 'cachedMetaData' | 'data'>): boolean => {
      if (!companyId) return false;
      const ctx = resolveChannelContext(channel);
      if (!ctx) return false;
      if (!ctx.companyId || ctx.companyId !== companyId) return false;

      if (documentId) {
        // Document page: match exact companyId + documentId
        return String(ctx.documentId) === String(documentId);
      }
      // Company page: match all channels with this companyId (including document-level channels)
      return true;
    },
    [companyId, documentId],
  );

  // Re-apply the search client-side to drop one class of Sendbird false positive: its
  // server-side `member_nickname` filter matches ANY member including the current user, so
  // typing part of your own name would otherwise return every chat you are in. Match instead
  // against the group/custom name OR any OTHER participant's name — this keeps renamed groups
  // findable by member (DA-19469) while still excluding the searcher's own name (the intended
  // scope of DA-19452). Pass the participant store so member names resolve to app usernames
  // rather than Sendbird's frequently-empty nicknames (DA-19440).
  const matchesSearch = React.useCallback(
    (channel: ChannelLike): boolean => channelMatchesQuery(channel, userProfile.id, chatParticipants, debouncedSearch),
    [debouncedSearch, userProfile.id, chatParticipants],
  );

  // User directory (DA-19646): eligible company peers to start a p2p chat with, shown beneath the
  // chat list on the current-context tab. `isCreating` drives the conversation-area loader
  // (forwarded via onCreatingChange, same channel the modal create flow uses) and locks rows.
  //
  // No create-nonce/remount is needed: a directory-created channel carries its context in the
  // atomic `data` string (written inside createChannel), which resolveChannelContext reads FIRST —
  // so matchesCurrentContext passes the instant the channel renders, even before the follow-up
  // createMetaData persists (DA-19646). The list re-renders on the collection's own add event and
  // the new row shows without any forced re-query.
  const {
    isFullyLoaded: isDirectoryLoaded,
    eligibleUsers,
    isCreating: isDirectoryCreating,
    startChat,
  } = useChatUserDirectory({
    searchText: debouncedSearch,
    onSelect,
    // Only drain the context-scoped collection on the current-context tab, where the directory is
    // rendered — the "All" tab never shows it, so skip the work and SDK subscriptions there.
    enabled: isCurrentContextFilter,
  });

  useEffect(() => {
    onCreatingChange?.(isDirectoryCreating);
  }, [isDirectoryCreating, onCreatingChange]);

  const handleCloseCreateModal = React.useCallback(() => setShowCreateModal(false), []);
  const handleOpenCreateModal = React.useCallback(() => setShowCreateModal(true), []);

  // `isCtx` is passed by the firing list, not read from the global `activeFilter`: with multiple
  // lists kept mounted, the current-context guard must reflect the list that fired the event, not
  // whichever tab happens to be active (matches the per-list guard in renderChannelPreview).
  const handleChannelSelect = React.useCallback(
    (channel: GroupChannel | null, isCtx: boolean) => {
      if (!channel?.url) return;
      // When the current-context list fires, reject channels that don't match the context
      // (Sendbird may auto-select a hidden channel that renderChannelPreview filters out).
      if (isCtx && !matchesCurrentContext(channel)) return;
      // Likewise reject a channel that renderChannelPreview hides because it does not match the
      // search — by neither its title nor any non-self participant (DA-19469).
      if (!matchesSearch(channel)) return;
      onSelect(channel.url);
    },
    [onSelect, matchesCurrentContext, matchesSearch],
  );

  const handleSearchChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  }, []);

  const handleFilterChange = React.useCallback((_: React.MouseEvent<HTMLElement> | null, value: string | null) => {
    if (!value) return;
    setActiveFilter(value);
    // Mount the tab's list on first visit and keep it alive thereafter (lazy keep-alive).
    setMountedFilters((prev) => (prev.has(value) ? prev : new Set(prev).add(value)));
  }, []);

  // Two-step delete: row's 3-dot menu stages the channel URL, then the confirm dialog
  // actually deletes. `channel.delete()` destroys the channel and its message history
  // for ALL members and is irreversible, so we never trigger it directly from the menu
  // click — it always goes through the confirmation dialog.
  const handleDeleteChannel = React.useCallback((channelUrl: string) => {
    setPendingDeleteUrl(channelUrl);
  }, []);

  // Ignore close/cancel/backdrop while a delete is in flight so the dialog can't be
  // dismissed mid-request (it closes itself on success in handleConfirmDelete).
  const handleCancelDelete = React.useCallback(() => {
    if (isDeleting) return;
    setPendingDeleteUrl(null);
  }, [isDeleting]);

  const handleConfirmDelete = React.useCallback(async () => {
    if (!sdk || !pendingDeleteUrl || isDeleting) return;
    const channelUrl = pendingDeleteUrl;
    // Keep the dialog open until the delete resolves. Only close it on success —
    // if `getChannel()`/`delete()` throws, the dialog stays open so the user sees
    // the action didn't complete and can retry or cancel. `isDeleting` guards
    // against a double-confirm while the request is in flight.
    setIsDeleting(true);
    try {
      const ch = await sdk.groupChannel.getChannel(channelUrl);
      await ch.delete();
      // If the deleted channel was selected, clear selection
      if (channelUrl === selectedChannelUrl) {
        onSelect('');
      }
      setPendingDeleteUrl(null);
    } catch (e) {
      console.error('[ChannelList] Failed to delete channel:', e);
    } finally {
      setIsDeleting(false);
    }
  }, [sdk, pendingDeleteUrl, isDeleting, selectedChannelUrl, onSelect]);

  const handleCreateChannelClick = React.useCallback(
    async (users: string[]): Promise<void> => {
      if (!sdk) return;

      const { customType, metadata, data } = buildChannelContext(
        Apps.AP,
        companyId,
        documentId,
        users.length === 1 ? 'direct' : 'group',
      );

      if (users.length === 1) {
        const query = sdk.groupChannel.createMyGroupChannelListQuery({
          customTypesFilter: [Apps.AP],
          includeEmpty: true,
          userIdsFilter: {
            userIds: [...users, userProfile.id],
            includeMode: false,
            queryType: QueryType.AND,
          },
        });

        let existing: GroupChannel | undefined;
        while (query.hasNext) {
          const page = await query.next();
          existing = page.find((ch) => {
            const ctx = resolveChannelContext(ch);
            return (
              ctx?.type === 'direct' &&
              String(ctx?.companyId ?? '') === String(companyId ?? '') &&
              String(ctx?.documentId ?? '') === String(documentId ?? '')
            );
          });
          if (existing) break;
        }

        if (existing) {
          onSelect(existing.url);
          return;
        }
      }

      // Dual-write, part 1: `data` (legacy JSON context string) is written atomically at create
      // time for backward compatibility with non-frontend `data` consumers. The frontend does
      // NOT read it back — context is resolved from `metadata` below.
      const channel = await sdk.groupChannel.createChannel({
        invitedUserIds: [...users, userProfile.id],
        operatorUserIds: [userProfile.id],
        customType,
        isDistinct: false,
        data,
      });

      // Dual-write, part 2: the SDK createChannel takes no metadata argument, so context is also
      // attached to `metadata` in a second call — this is the field the frontend reads. Block
      // selection on this write: a channel selected without metadata context would break the
      // current-context filter, breadcrumbs, and deep-links. On failure the error propagates
      // (caught + logged in handleCreateClick) and the uncontextualized channel is NOT selected;
      // the already-persisted `data` context is left intact on the channel.
      await channel.createMetaData(metadata);

      onSelect(channel.url);
    },
    [sdk, companyId, documentId, userProfile.id, onSelect],
  );

  const handleCreateClick = React.useCallback(
    async ({ users }: { users: string[] }): Promise<void> => {
      onCreatingChange?.(true);
      try {
        await handleCreateChannelClick(users);
        // Only close the modal on success — if channel creation or the metadata write
        // fails, keep it open so the user can retry rather than silently dropping the chat.
        setShowCreateModal(false);
      } catch (e) {
        // Single log point for the whole create flow (dedupe query, createChannel, and the
        // createMetaData write all propagate here). Swallow after logging so the awaited promise
        // does not surface as an unhandled rejection; the still-open modal signals the failure
        // to the user for retry.
        console.error('[ChannelList] Create chat failed:', e);
      } finally {
        onCreatingChange?.(false);
      }
    },
    [handleCreateChannelClick, onCreatingChange],
  );

  return (
    <ChannelListRoot direction="column" sx={sx}>
      {/* Static header rendered outside GroupChannelList so it never remounts on search/filter key change */}
      <Box sx={{ flexShrink: 0 }}>
        <ChatListHeader>
          <ChannelListHeaderUsername>
            {formatMessage({ id: 'chat.channelList.title', defaultMessage: 'Conversations' })}
            {totalUnreadCount > 0 && (
              <UnreadCountBadge>{totalUnreadCount > 99 ? '99+' : totalUnreadCount}</UnreadCountBadge>
            )}
            {/* zIndex 1700 lifts the Beta tooltip Popper above the chat Drawer (1600),
                matching the New-chat button tooltip below. */}
            <BetaBadge sx={{ ml: 1 }} tooltipSlotProps={createTooltipSlotProps} />
          </ChannelListHeaderUsername>
          {/* The Tooltip Popper defaults to theme.zIndex.tooltip (1500), but the chat
              panel is a MUI Drawer modal stacked at zIndex 1600, so without lifting the
              Popper the tooltip renders behind the drawer and is invisible — including
              the disabled-state explanation on the "New chat" button (DA-19426). Lift it
              to 1700 to match the chat header tooltips and dialogs. */}
          <Tooltip t={{ id: createTooltipId }} slotProps={createTooltipSlotProps}>
            <span>
              <IconButton sx={{ width: 32, height: 32 }} onClick={handleOpenCreateModal} disabled={!canCreateChannel}>
                <AddCommentOutlinedIcon
                  sx={{ fontSize: 22, color: canCreateChannel ? 'primary.main' : 'action.disabled' }}
                />
              </IconButton>
            </span>
          </Tooltip>
        </ChatListHeader>
        <ChannelListSearchBox>
          <TextField
            size="small"
            placeholder={formatMessage({ id: 'chat.channelList.searchPlaceholder', defaultMessage: 'Search' })}
            value={search}
            fullWidth
            onChange={handleSearchChange}
            InputProps={{
              endAdornment: search ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    edge="end"
                    aria-label={formatMessage({
                      id: 'chat.channelList.clearSearch',
                      defaultMessage: 'Clear search',
                    })}
                    onClick={() => setSearch('')}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
            }}
          />
        </ChannelListSearchBox>
        {hasCompanyContext && (
          <ChannelListFilterBox>
            <FilterChipBar>
              <FilterChip
                label={
                  <>
                    {formatMessage({ id: 'chat.channelList.filterAll', defaultMessage: 'All conversations' })}
                    {totalUnreadCount > 0 && (
                      <UnreadPill selected={activeFilter === 'ALL'}>{totalUnreadCount}</UnreadPill>
                    )}
                  </>
                }
                variant={activeFilter === 'ALL' ? 'filled' : 'outlined'}
                color={activeFilter === 'ALL' ? 'primary' : 'default'}
                onClick={() => handleFilterChange(null, 'ALL')}
              />
              <FilterChip
                icon={documentId ? <ReceiptOutlinedIcon /> : <FolderOpenOutlinedIcon />}
                label={
                  <>
                    {contextChipLabel}
                    {contextUnread > 0 && (
                      <UnreadPill selected={activeFilter === CURRENT_CONTEXT_FILTER}>{contextUnread}</UnreadPill>
                    )}
                  </>
                }
                variant={activeFilter === CURRENT_CONTEXT_FILTER ? 'filled' : 'outlined'}
                color={activeFilter === CURRENT_CONTEXT_FILTER ? 'primary' : 'default'}
                onClick={() => handleFilterChange(null, CURRENT_CONTEXT_FILTER)}
              />
            </FilterChipBar>
          </ChannelListFilterBox>
        )}
      </Box>

      {/* Shared single scroll only on the current-context tab AND only after all channels are
          retrieved (isDirectoryLoaded). Before that — and always on the All tab — the panel is a
          passthrough and the chat list keeps its own bounded scroll + infinite pagination. */}
      <PanelScrollArea active={isCurrentContextFilter && isDirectoryLoaded}>
      {/* "Chats" section header (DA-19646). Shown only on the current-context tab, where the
          "Users" directory also appears beneath the list — the two inline headers separate the
          chat list from the user directory (per the panel design). The "All" tab keeps the plain
          list with no section headers. */}
      {isCurrentContextFilter && (
        <SectionHeader>
          {formatMessage({ id: 'chat.channelList.sectionChats', defaultMessage: 'Conversations' })}
        </SectionHeader>
      )}

      {/* One GroupChannelList is kept mounted per visited filter and only the active one is
          shown (display toggle). Each list's `key` (listKey below) is stable across tab
          switches — the filter token is constant per list — so switching tabs no longer
          remounts Sendbird's list and each tab keeps its own scroll position and already-loaded
          pages (DA-19468). A remount (reset to top) is triggered only by a search change and,
          for the current-context tab, by a change of the active company/document — because then
          the tab's rows mean something different and stale scroll/pages must not carry over.
          Filtering by mountedFilters gives lazy keep-alive: a tab's list is created on first
          visit, not up front. */}
      {visibleFilters
        .filter((filter) => mountedFilters.has(filter))
        .map((filter) => {
          const isCtx = filter === CURRENT_CONTEXT_FILTER;
          // Current-context rows are narrowed to the active company/document client-side, so its
          // list must remount when that context changes (not just on search). ALL is
          // context-independent and only remounts on search.
          const listKey = isCtx
            ? `${filter}-${companyId ?? ''}-${documentId ?? ''}-${debouncedSearch}`
            : `${filter}-${debouncedSearch}`;
          return (
            <GroupChannelListWrapper
              key={filter}
              // Switch this list to content-height only when it is the current-context list AND
              // all its channels are loaded — never while pagination could still run.
              sharedScroll={isCtx && isDirectoryLoaded}
              // Hide Sendbird's built-in placeholder only on the current-context list (where our own
              // EmptyChatsText renders). The All list keeps Sendbird's placeholder as its empty state.
              isCtx={isCtx}
              sx={{ display: activeFilter === filter ? 'flex' : 'none' }}
            >
              <GroupChannelList
                key={listKey}
                channelListQueryParams={buildQueryParams(filter)}
                // Per DA-19067, no chat opens by default: opening chat shows the list (mobile)
                // or the "Create or select a chat" empty panel (desktop), never an auto-opened
                // channel. Sendbird otherwise auto-selects channels[0] on mount and fires
                // onChannelSelect — flaky because groupChannels[0] is the most-recently-active
                // channel globally, so whether it "sticks" depends on load timing and the
                // current-context guard (opened first chat inconsistently: DA-19475 on mobile,
                // same on desktop). Disable it everywhere for deterministic list-first behaviour.
                disableAutoSelect
                onChannelSelect={(channel) => handleChannelSelect(channel, isCtx)}
                onChannelCreated={() => {}}
                // renderHeader is a stable mount point inside the list provider: ChannelListEmptyState
                // reads the list's collection there and reports emptiness upward. Only the
                // current-context list drives the placeholder (it is shown on the context tab only),
                // so the ALL list's report is ignored (DA-19567 / DA-19646).
                renderHeader={() => (
                  <ChannelListEmptyState
                    isCtx={isCtx}
                    matchesCurrentContext={matchesCurrentContext}
                    matchesSearch={matchesSearch}
                    onEmptyChange={isCtx ? setIsContextChatListEmpty : noop}
                  />
                )}
                renderChannelPreview={({ channel }) => {
                  if (isCtx && !matchesCurrentContext(channel)) {
                    return <></>;
                  }

                  // Filter out false-positive matches from Sendbird's member_nickname search:
                  // keep only channels whose displayed title matches the query (DA-19452).
                  if (!matchesSearch(channel)) {
                    return <></>;
                  }

                  return (
                    <ChannelPreviewItem
                      channel={channel}
                      isSelected={channel.url === selectedChannelUrl}
                      onClick={() => onSelect(channel.url)}
                      onPreviewDocument={(companyId, documentId) => setPreviewDoc({ companyId, documentId })}
                      onDeleteChannel={handleDeleteChannel}
                      isMobile={isMobile}
                    />
                  );
                }}
              />
            </GroupChannelListWrapper>
          );
        })}

      {/* "No chats" placeholder (DA-19646): rendered here as a normal sibling — NOT portaled into
          Sendbird's list body — so its bottom divider (EmptyChatsText) reliably separates the Chats
          section from the Users section below. Shown only on the current-context tab, only once all
          channels are retrieved (isDirectoryLoaded), and when the list is then empty
          (ChannelListEmptyState reports emptiness up via onEmptyChange). Gating on isDirectoryLoaded
          avoids a premature empty-state flash while the context drain is still in progress (spec:
          the placeholder must not show while retrieval is incomplete). */}
      {isCurrentContextFilter && isDirectoryLoaded && isContextChatListEmpty && (
        <EmptyChatsText>{formatMessage({ id: 'chat.channelList.noChats', defaultMessage: 'No conversations' })}</EmptyChatsText>
      )}

      {/* User directory (DA-19646): shown beneath the chat list ONLY on the current-context tab
          and ONLY once every channel for the context has been retrieved (isDirectoryLoaded) — so
          the excluded-user set is complete before any user is offered. Never rendered on "All".
          The section itself keeps its "Users" header even when there are zero eligible users. */}
      {isCurrentContextFilter && isDirectoryLoaded && (
        <UserDirectorySection users={eligibleUsers} onStartChat={startChat} isCreating={isDirectoryCreating} />
      )}
      </PanelScrollArea>

      {showCreateModal && (
        <>
          {/* onSelect is called inside handleCreateChannelClick; onChannelCreated is not invoked when onCreateChannelClick is provided */}
          <CreateChannel
            onCancel={handleCloseCreateModal}
            onChannelCreated={handleCloseCreateModal}
            renderStepOne={() => <CreateChannelModal onCancel={handleCloseCreateModal} />}
            onCreateChannelClick={handleCreateClick}
          />
        </>
      )}

      {previewDoc && (
        <ChatDocumentPreviewModal
          companyId={previewDoc.companyId}
          documentId={previewDoc.documentId}
          onClose={() => setPreviewDoc(null)}
          isMobile={isMobile}
        />
      )}

      <Dialog
        open={pendingDeleteUrl !== null}
        onClose={handleCancelDelete}
        onOk={handleConfirmDelete}
        title={formatMessage({
          id: 'chat.channelList.deleteConfirm.title',
          defaultMessage: 'Delete chat?',
        })}
        okText={formatMessage({
          id: 'chat.channelList.deleteConfirm.confirm',
          defaultMessage: 'Delete',
        })}
        cancelText={formatMessage({ id: 'button.cancel', defaultMessage: 'Cancel' })}
        okBtnProps={{ color: 'error', disabled: isDeleting }}
        withCloseBtn
        sx={{ zIndex: 'chatPopup' }}
      >
        {formatMessage({
          id: 'chat.channelList.deleteConfirm.body',
          defaultMessage:
            'This chat and all its messages will be permanently deleted for everyone. This action cannot be undone.',
        })}
      </Dialog>
    </ChannelListRoot>
  );
};

export default ChannelListPanel;
